import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Pencil, Trash2, Flag, Info, Lock, Unlock, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { FilterByCriteria, ShippingRule, ShippingRuleCondition } from '@/types';
import { useToggleShippingRule, useDeleteShippingRule } from '@/hooksApi/useShippingRule';
import { formatRuleSummary } from '@/utils/formatRuleSummary';
import type { ProvinceOption } from '@/utils/provinceLookup';
import { ShippingRuleCard } from './ShippingRuleCard';
import { TablePagination } from './TablePagination';
import { ShippingRulesTableLoading } from './ShippingRulesTableLoading';

const carrierColorPalette = [
  'hsl(210,60%,50%)',
  'hsl(260,50%,55%)',
  'hsl(25,80%,50%)',
  'hsl(145,50%,40%)',
  'hsl(0,65%,50%)',
  'hsl(190,60%,42%)',
];

const colorForSlug = (slug: string): string => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) & 0xffff;
  return carrierColorPalette[h % carrierColorPalette.length];
};

const getInitials = (name: string) => {
  const words = name.split(/(?=[A-Z])|\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// OBS-NEW-5 — small chip surfacing the rule's filter_by_criteria so
// merchants can tell at a glance whether a rule auto-picks cheapest /
// most-reliable / fastest. Returns null when no filter is set.
function FilterChip({ filter, t }: { filter: FilterByCriteria; t: (key: string) => string }) {
  if (!filter) return null;
  const key =
    filter === 'cheapest'
      ? 'filter_cheapest'
      : filter === 'most_reliable'
        ? 'filter_most_reliable'
        : 'filter_fastest';
  const tone =
    filter === 'cheapest'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : filter === 'fastest'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-violet-50 text-violet-700 border-violet-200';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}
    >
      {t(key)}
    </span>
  );
}

// SR-OVR — surface each rule's `is_overridable` at a glance so merchants can
// tell, without opening the rule, whether the carrier is a suggestion or
// enforced. `is_overridable === true` → "Overridable" (merchant may pick a
// different carrier at order time); anything else → "Enforced" (the selected
// carrier is forced). Backend default is strict (false), so undefined reads as
// Enforced. Pill style matches FilterChip for visual consistency.
function OverridableBadge({
  overridable,
  t,
}: {
  overridable?: boolean;
  t: (key: string) => string;
}) {
  const isOverridable = overridable === true;
  const tone = isOverridable
    ? 'bg-sky-50 text-sky-700 border-sky-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  const label = isOverridable
    ? t('shipping_rules.overridable_badge_yes')
    : t('shipping_rules.overridable_badge_no');
  const tip = isOverridable
    ? t('shipping_rules.overridable_yes_tip')
    : t('shipping_rules.overridable_no_tip');
  const Icon = isOverridable ? Unlock : Lock;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`rule-overridable-${isOverridable ? 'yes' : 'no'}`}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}
          >
            <Icon className='h-3 w-3 shrink-0' aria-hidden='true' />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side='top' className='max-w-[220px] text-xs leading-relaxed'>
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function pickConditionForColumn(
  conditions: ShippingRuleCondition[] | undefined,
  field: 'source' | 'destination',
  provinces: ProvinceOption[] = [],
): string {
  const hit = conditions?.find((c) => c.field === field);
  if (!hit) return '—';
  const decode = (v: unknown): string => {
    const hit = provinces.find((p) => p.value === String(v));
    return hit ? hit.label : String(v);
  };
  const v = hit.value;
  if (Array.isArray(v)) return v.map(decode).join(', ');
  return decode(v);
}

function DescriptionCell({ summary }: { summary: string }) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight);
  }, [summary]);

  const colorClass = 'bg-[hsl(var(--sd-desc-bg-blue))] text-[hsl(var(--sd-desc-text-blue))]';
  const tooltipColorClass =
    'bg-[hsl(var(--sd-desc-bg-blue))] text-[hsl(var(--sd-desc-text-blue))] border-[hsl(var(--sd-desc-bg-blue))]';

  const content = (
    <div className='relative overflow-hidden'>
      <div
        ref={textRef}
        className={`rounded-lg px-3 py-[6px] text-xs leading-relaxed line-clamp-2 flex items-start gap-2 ${colorClass}`}
      >
        <Info className='h-3.5 w-3.5 shrink-0 mt-0.5 opacity-70' />
        <span>{summary}</span>
      </div>
    </div>
  );

  if (!isClamped) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent
        side='top'
        className={`max-w-[320px] text-xs leading-relaxed shadow-lg z-50 p-3 rounded-lg ${tooltipColorClass}`}
      >
        {summary}
      </TooltipContent>
    </Tooltip>
  );
}

interface ShippingRulesTableProps {
  rules: ShippingRule[];
  isLoading?: boolean;
  pageSize?: number;
  onEdit?: (rule: ShippingRule) => void;
  provinces?: ProvinceOption[];
  // Item 1 — Lock Shipping Rules. When `locked` is true ALL rows are
  // view-only (toggle/edit/delete disabled) regardless of rule_origin —
  // the backend also 403s every write. Per-row `isAgent` locking still
  // applies independently for agent-origin rules.
  locked?: boolean;
}

export function ShippingRulesTable({
  rules,
  isLoading = false,
  pageSize = 10,
  onEdit,
  provinces = [],
  locked = false,
}: ShippingRulesTableProps) {
  const { t } = useTranslation();
  const { mutate: toggleMutate } = useToggleShippingRule();
  const { mutate: deleteMutate } = useDeleteShippingRule();

  const [currentPage, setCurrentPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<ShippingRule | null>(null);
  const totalRecords = rules.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const visible = rules.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const onToggle = (rule: ShippingRule) => {
    // Block writes against agent-origin rules (RS-32) and against every rule
    // when the account is locked (Item 1). Server 403s either way — defense
    // in depth.
    if (rule.rule_origin === 'agent' || locked) return;
    toggleMutate(rule.uid);
  };

  const onDelete = (rule: ShippingRule) => {
    if (rule.rule_origin === 'agent' || locked) return;
    // Custom AlertDialog instead of window.confirm — confirm() blocks the JS
    // event loop and breaks browser-automation flows; the Radix dialog is a
    // proper modal with focus trap, keyboard nav, and i18n.
    setPendingDelete(rule);
  };

  const confirmDelete = () => {
    if (pendingDelete) {
      deleteMutate(pendingDelete.uid);
      setPendingDelete(null);
    }
  };

  if (isLoading) return <ShippingRulesTableLoading />;

  if (totalRecords === 0) {
    return (
      <div className='p-8 text-center text-sm text-sd-muted-foreground'>
        {t('shipping_rules.no_rules_yet')}
      </div>
    );
  }

  return (
    <div>
      {/* Mobile: Cards */}
      <div className='grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:hidden'>
        {visible.map((rule) => (
          <ShippingRuleCard
            key={rule.uid}
            rule={rule}
            onToggle={() => onToggle(rule)}
            onDelete={() => onDelete(rule)}
            // The card disables actions per-row for agent rules (RS-32) and
            // for every row when `locked` (Item 1).
            onEdit={onEdit ? () => onEdit(rule) : undefined}
            locked={locked}
          />
        ))}
      </div>

      {/* Desktop: Table */}
      <div className='hidden lg:block overflow-x-auto'>
        <table className='w-full text-sm table-fixed'>
          <colgroup>
            <col className='w-[72px]' />
            {/* SR-FB4 (May 2026 feedback) — priority is the tie-breaker
                between matching rules; merchants asked to see it at a
                glance instead of opening each rule to check. */}
            <col className='w-[80px]' />
            <col className='w-[14%]' />
            {/* SR-OVR — overridable / enforced status, surfaced as a column.
                Sized so the longest pill ("قابلة للتغيير" in Arabic, ~95px)
                fits on one line without leaving a large empty gap. */}
            <col className='w-[148px]' />
            <col className='w-[22%]' />
            <col className='w-[14%]' />
            <col />
            <col className='w-[110px]' />
          </colgroup>
          <thead>
            <tr className='border-b border-sd-border'>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('active')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('shipping_rules.priority_label')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('name')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('shipping_rules.overridable_column')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('carriers')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('destination')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('info_label')}
              </th>
              <th className='px-5 py-3.5 text-start text-xs font-semibold uppercase tracking-wider text-sd-muted-foreground'>
                {t('action')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((rule) => {
              const isAgent = rule.rule_origin === 'agent';
              // Item 1 — locked accounts make every row view-only.
              const readOnly = isAgent || locked;
              const carriers = rule.carriers ?? [];
              const summary = formatRuleSummary(rule.conditions ?? [], t as any, provinces);
              return (
                <tr
                  key={rule.uid}
                  className='border-b border-sd-border transition-colors hover:bg-sd-muted/40'
                >
                  <td className='px-5 py-2'>
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={() => onToggle(rule)}
                      disabled={readOnly}
                      className='data-[state=checked]:bg-sd-primary'
                    />
                  </td>
                  <td className='px-5 py-2' data-testid={`rule-priority-${rule.uid}`}>
                    <span className='inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-md bg-sd-muted text-xs font-semibold text-sd-foreground'>
                      {rule.priority}
                    </span>
                  </td>
                  <td className='px-5 py-2 font-medium truncate'>
                    <div className='flex items-center gap-1.5 flex-wrap'>
                      <span className='truncate'>{rule.name}</span>
                      <FilterChip filter={rule.filter_by_criteria} t={t as (k: string) => string} />
                      {isAgent && (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Lock className='h-3.5 w-3.5 shrink-0 text-sd-muted-foreground' />
                            </TooltipTrigger>
                            <TooltipContent side='top' className='text-xs'>
                              {t('shipping_rules.managed_by_boxy')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </td>
                  <td className='px-5 py-2'>
                    <OverridableBadge
                      overridable={rule.is_overridable}
                      t={t as (k: string) => string}
                    />
                  </td>
                  <td className='px-5 py-2'>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className='flex items-center w-fit'>
                            {carriers.slice(0, 3).map((c, i) => (
                              <div
                                key={c.uid}
                                className='flex h-7 w-7 items-center justify-center rounded-full border-2 border-sd-card text-white text-[9px] font-bold'
                                style={{
                                  // Logical so the avatar overlap stacks from
                                  // the correct side in RTL (Arabic) too.
                                  marginInlineStart: i > 0 ? -8 : 0,
                                  zIndex: 10 - i,
                                  backgroundColor: colorForSlug(c.slug),
                                }}
                              >
                                {getInitials(c.title)}
                              </div>
                            ))}
                            {carriers.length > 3 && (
                              <div
                                className='flex h-7 w-7 items-center justify-center rounded-full border-2 border-sd-card bg-sd-muted text-[10px] font-bold'
                                style={{ marginInlineStart: -8, zIndex: 6 }}
                              >
                                +{carriers.length - 3}
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side='bottom' sideOffset={4} className='text-xs'>
                          {carriers.map((c) => c.title).join(', ')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className='px-5 py-2'>
                    {/* Item 3 — list shows the destination only; the source
                        condition still exists on the rule, it is just not
                        surfaced in this column. */}
                    <span className='flex items-center gap-2 text-sm'>
                      <Flag className='h-3.5 w-3.5 shrink-0 text-sd-dest-green fill-sd-dest-green' />
                      {pickConditionForColumn(rule.conditions, 'destination', provinces)}
                    </span>
                  </td>
                  <td className='px-5 py-2'>
                    <DescriptionCell summary={summary} />
                  </td>
                  <td className='px-5 py-2'>
                    {/* Action buttons render disabled for agent rows (RS-32)
                        and for every row when the account is locked (Item 1).
                        The server 403s either way — this is the UI guard. */}
                    <div className='flex gap-2'>
                      <button
                        onClick={() => !readOnly && onEdit?.(rule)}
                        disabled={readOnly || !onEdit}
                        title={readOnly ? String(t('shipping_rules.managed_by_boxy')) : undefined}
                        className='rounded-lg border border-sd-border p-2 transition-colors hover:bg-sd-muted disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        <Pencil className='h-4 w-4 text-sd-muted-foreground' />
                      </button>
                      <button
                        onClick={() => !readOnly && onDelete(rule)}
                        disabled={readOnly}
                        title={readOnly ? String(t('shipping_rules.managed_by_boxy')) : undefined}
                        className='rounded-lg border border-sd-border p-2 transition-colors hover:bg-sd-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        <Trash2 className='h-4 w-4 text-sd-muted-foreground' />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalRecords > pageSize && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          visibleCount={visible.length}
          onPageChange={setCurrentPage}
        />
      )}

      {/* SR-FB7 (May 2026 feedback) — delete confirmation modal.
          (1) Warning-yellow (not red/orange) CTA — the operation is a soft
              delete (recoverable) so the visual matches the actual blast
              radius. Using yellow-400 → 500 instead of amber so the color
              reads as clearly yellow rather than orange.
          (2) Modern modal shell to match ShippingRuleBuilderModal: 2xl
              rounded corners, white card on darkened backdrop, AlertCircle
              icon next to the title, real Cancel button (outlined) instead
              of plain text, sticky footer with proper button hierarchy. */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className='max-w-md p-0 overflow-hidden rounded-2xl border border-sd-border bg-sd-card shadow-2xl gap-0'>
          <AlertDialogHeader className='px-6 pt-6 pb-2'>
            <div className='flex items-start gap-3'>
              {/* Icon tinted with the design-system primary (brand yellow
                  hsl(55 82% 61%) ≈ #EDE04A). Using semantic tokens keeps
                  it in sync if the brand ever shifts. */}
              <div className='shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-sd-primary/15 text-sd-primary-foreground ring-4 ring-sd-primary/5'>
                <AlertTriangle className='h-5 w-5 text-sd-primary-foreground' aria-hidden='true' />
              </div>
              <div className='min-w-0 flex-1 space-y-1'>
                <AlertDialogTitle className='text-base leading-tight'>
                  {t('shipping_rules.delete_title')}
                </AlertDialogTitle>
                <AlertDialogDescription className='text-xs leading-relaxed'>
                  {t('shipping_rules.delete_confirm')}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className='px-6 py-4 mt-2 border-t border-sd-border bg-sd-muted/30 sm:space-x-2 rtl:sm:space-x-reverse'>
            <AlertDialogCancel className='h-9 px-5 text-xs font-medium mt-0'>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid='delete-rule-confirm'
              className='h-9 px-5 text-xs font-semibold bg-sd-primary text-sd-primary-foreground hover:bg-sd-primary/90 focus-visible:ring-sd-primary mt-0'
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
